// Globals
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import {
  StyledContentBlock,
  StyledLabel,
  StyledPaymentBlock,
  StyledPaymentStatusRow,
  StyledValue,
} from './AdditionalInfoContentBlockStyles';
import AdditionalInfoContentBlock from './AdditionalInfoContentBlock';
import OrderStatusBadge from '../OrderStatusBadge/OrderStatusBadge';

// Models
import { IOrderDetails, OrdersPaymentStatus } from '@/models/orders.model';
import { DateFormats } from '@/constants/dates';
import { formatDate } from '@/utils';

// Helpers
import { getOrderPaymentData, getPaymentChannelData, getPaymentsInfo } from '../../helpers';

interface PaymentInfoContentBlockProps {
  data?: IOrderDetails | null;
}

const PaymentInfoContentBlock = ({ data }: PaymentInfoContentBlockProps) => {
  const { t } = useTranslation(['orders']);
  const paymentsInfo = getPaymentsInfo(data?.payments_info ?? []);

  const mappedPayments = useMemo(() => {
    return paymentsInfo?.map((item) => {
      const paymentData = getOrderPaymentData(t, item, data?.payment_type);
      return (
        <StyledPaymentBlock key={item.legalType}>
          {paymentData.map((paymentItem) => (
            <AdditionalInfoContentBlock
              key={item.legalType}
              label={paymentItem.label}
              value={paymentItem.value}
              tooltip={paymentItem.tooltip}
            />
          ))}
        </StyledPaymentBlock>
      );
    });
  }, [data]);

  if (!data) return null;

  const paymentChannelLabelKey = getPaymentChannelData(data.payment_channel);

  return (
    <StyledContentBlock>
      <div>
        <StyledLabel>{t('orders_list_payment_type')}</StyledLabel>
        <StyledValue>{t(`orders_list_payment_${data?.payment_type}`)}</StyledValue>
      </div>
      <div>
        <StyledLabel>{t('orders_list_payment_channel')}</StyledLabel>
        <StyledValue>{paymentChannelLabelKey ? t(paymentChannelLabelKey) : '—'}</StyledValue>
      </div>
      {mappedPayments}
      <div>
        <StyledLabel>{t('order_details_payment_status')}</StyledLabel>
        <StyledValue>
          <StyledPaymentStatusRow>
            <OrderStatusBadge t={t} record={data} isPaymentStatus wrap />
            {data.payment_status === OrdersPaymentStatus.PAID && data.payment_date ? (
              <span>{formatDate(data.payment_date, DateFormats.EUROPEAN)}</span>
            ) : null}
          </StyledPaymentStatusRow>
        </StyledValue>
      </div>
    </StyledContentBlock>
  );
};

export default PaymentInfoContentBlock;
